/**
 * P3TRO LEGEND - Shopify Theme JavaScript
 * Modern, modular theme functionality
 */

// ============================================================================
// CONFIGURATION & UTILITIES
// ============================================================================

const ThemeConfig = {
  selectors: {
    siteHeader: '#site-header',
    menuBtn: '[data-action="menu"]',
    menuOverlay: '#menu-overlay',
    searchBtn: '[data-action="search"]',
    searchOverlay: '#search-overlay',
    cartBtn: '[data-action="cart"]',
    cartDrawer: '#cart-drawer',
    addToCart: '[data-action="add-to-cart"]',
    hero: '[data-hero]',
    muteBtn: '[data-action="mute"]',
  },
  classes: {
    isOpen: 'is-open',
    isScrolled: 'is-scrolled',
    menuOpen: 'menu-open',
    lbOpen: 'lb-open',
  },
};

// Debounce utility
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Format a price (in cents) using the shop's real money_format setting.
 * Shopify's money_format can contain any of several placeholder tokens
 * depending on the store's currency formatting (not just {{amount}}) —
 * e.g. {{amount_with_comma_separator}} for "1.234,56" style formatting.
 * A naive string-replace on {{amount}} alone leaves the raw placeholder
 * text on screen whenever the shop uses a different token.
 */
function formatWithDelimiters(cents, precision, thousands, decimal) {
  precision = precision === undefined ? 2 : precision;
  thousands = thousands === undefined ? ',' : thousands;
  decimal = decimal === undefined ? '.' : decimal;

  if (isNaN(cents) || cents === null) return '0';

  const number = (cents / 100).toFixed(precision);
  const parts = number.split('.');
  const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
  const decimalPart = parts[1] ? decimal + parts[1] : '';

  return dollars + decimalPart;
}

window.formatMoney = function formatMoney(cents, format) {
  const formatString =
    format ||
    (window.Shopify && window.Shopify.money_format) ||
    '€{{amount}}';

  const match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
  const token = match ? match[1] : 'amount';

  let value;
  switch (token) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return match ? formatString.replace(match[0], value) : formatString + value;
};

// ============================================================================
// MOTION HELPERS
// ============================================================================
// The stylesheet hides a set of elements (opacity:0 + translateY) under
// @media (prefers-reduced-motion: no-preference) so they can be revealed
// with a staggered entrance — mirroring the live site. gsap (self-hosted,
// loaded before this file) runs the entrance; if it is missing, reduced
// motion is on, or anything throws, `forceReveal` clears the hidden state
// immediately so nothing is ever left invisible.

const Motion = {
  get gsap() {
    return window.gsap || null;
  },
  reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,

  // Snap finished elements to their resting state and drop will-change.
  settle(nodes) {
    (nodes ? Array.from(nodes) : []).forEach((el) => {
      if (!el) return;
      el.classList.add('is-settled');
      el.style.transform = 'none';
      el.style.willChange = 'auto';
    });
  },

  // Make elements visible with no animation (fallback / reduced motion).
  forceReveal(nodes) {
    (nodes ? Array.from(nodes) : []).forEach((el) => {
      if (!el) return;
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.visibility = 'visible';
      el.classList.add('is-settled');
    });
  },

  // gsap.to that reveals `nodes` from the CSS-hidden state, then settles.
  reveal(nodes, opts) {
    const list = nodes ? Array.from(nodes) : [];
    if (!list.length) return;
    if (this.reduced || !this.gsap) {
      this.forceReveal(list);
      return;
    }
    const self = this;
    this.gsap.to(
      list,
      Object.assign(
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: 'power3.out',
          force3D: true,
          onComplete() {
            self.settle(list);
          },
        },
        opts || {}
      )
    );
  },
};

// ============================================================================
// PAGE ENTRANCE REVEAL
// ============================================================================

class RevealManager {
  constructor() {
    this.run();
  }

  run() {
    try {
      const page =
        document.querySelector('.page-view') || document.querySelector('main') || document.body;

      const dataReveal = document.querySelectorAll(
        '[data-reveal]:not(.hero-title):not(.story-open-copy [data-reveal])'
      );
      const cards = document.querySelectorAll('.product-card:not(.is-hidden)');
      const aboutCopy = document.querySelectorAll('.about-copy p');
      const pdpGallery = document.querySelectorAll('.pdp-gallery');
      const pdpInfo = document.querySelectorAll(
        '.pdp-info > *:not(.is-hidden):not(.pdp-related-mount)'
      );
      const contactBg = document.querySelectorAll('.page-contact-bg img');
      const muteBtn = document.querySelectorAll('.mute-btn');
      const heroTitle = document.querySelectorAll('.hero-title');
      // The story page runs its own pinned scroll timeline (sections/
      // story-petro.liquid) — leave its elements to that script.
      const lookbook = document.querySelectorAll('.lookbook-grid button');

      // Everything the stylesheet may have hidden — used for the failsafe.
      const all = []
        .concat(
          Array.from(dataReveal),
          Array.from(cards),
          Array.from(aboutCopy),
          Array.from(pdpGallery),
          Array.from(pdpInfo),
          Array.from(contactBg),
          Array.from(muteBtn),
          Array.from(heroTitle),
          Array.from(lookbook)
        );

      if (Motion.reduced || !Motion.gsap) {
        Motion.forceReveal(all);
        return;
      }

      Motion.reveal(dataReveal, { stagger: 0.1 });
      Motion.reveal(cards, { duration: 0.65, stagger: 0.05 });
      Motion.reveal(aboutCopy, { duration: 0.8, stagger: 0.12 });
      Motion.reveal(pdpGallery, {});
      Motion.reveal(pdpInfo, { duration: 0.6, stagger: 0.06 });
      Motion.reveal(heroTitle, {});
      Motion.reveal(lookbook, { duration: 0.65, stagger: 0.05 });
      if (muteBtn.length) Motion.gsap.to(muteBtn, { opacity: 1, duration: 0.6, delay: 0.6 });
      if (contactBg.length)
        Motion.gsap.to(contactBg, { opacity: 1, duration: 1, ease: 'power2.out' });

      // Failsafe: if any element is still invisible a moment later
      // (animation interrupted, tab backgrounded on load, etc.), show it.
      setTimeout(() => {
        all.forEach((el) => {
          if (el && !el.classList.contains('is-settled')) Motion.forceReveal([el]);
        });
      }, 2000);
    } catch (err) {
      console.error('[P3TRO LEGEND] RevealManager failed:', err);
      Motion.forceReveal(document.querySelectorAll('[data-reveal], .product-card, .pdp-gallery, .pdp-info > *, .hero-title, .mute-btn, .about-copy p, .page-contact-bg img'));
    }
  }
}

// ============================================================================
// PRODUCT-CARD HOVER  (shared by the shop grid, PDP related, search results)
// ============================================================================
// Exactly the live site's shop.js bindCardHovers: swap the image to the
// alternate shot and scale it 1 -> 1.05 over 0.6s (power2.out) on enter,
// back to 1 over 0.5s on leave. gsap drives it; without gsap it falls back
// to the stylesheet's `.product-card:hover img { transform: scale(1.04) }`.

window.P3TROCardHover = function (scope) {
  var root = scope || document;
  var cards = root.querySelectorAll('.product-card[data-hover-src], .product-card[data-base-src]');
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  cards.forEach(function (card) {
    if (card.dataset.hoverBound) return;
    card.dataset.hoverBound = '1';

    var img = card.querySelector('img');
    var base = card.getAttribute('data-base-src');
    var hover = card.getAttribute('data-hover-src');
    if (!img) return;

    if (hover && hover !== base) {
      var pre = new Image();
      pre.src = hover;
    }

    card.addEventListener('mouseenter', function () {
      if (hover && hover !== base) img.src = hover;
      if (reduced) return;
      var g = window.gsap;
      if (g) g.fromTo(img, { scale: 1 }, { scale: 1.05, duration: 0.6, ease: 'power2.out' });
      else img.style.transform = 'scale(1.05)';
    });

    card.addEventListener('mouseleave', function () {
      if (hover && hover !== base) img.src = base;
      if (reduced) return;
      var g = window.gsap;
      if (g) g.to(img, { scale: 1, duration: 0.5, ease: 'power2.out' });
      else img.style.transform = 'scale(1)';
    });
  });
};

// ============================================================================
// HEADER & SCROLL DETECTION
// ============================================================================

class HeaderManager {
  constructor() {
    this.header = document.querySelector(ThemeConfig.selectors.siteHeader);
    if (!this.header) return;

    this.scrollThreshold = 8;
    this.init();
  }

  init() {
    this.updateHeaderOnScroll();
    window.addEventListener(
      'scroll',
      debounce(() => this.updateHeaderOnScroll(), 10),
      { passive: true }
    );
    this.intro();
  }

  // Slide the header down, then stagger the P3TRO letters up and the
  // LEGEND letters up — matches the live site's header.intro() with the
  // SVG wordmark (.logo-letter groups).
  intro() {
    const letters = this.header.querySelectorAll('.logo-letter');
    const p3tro = this.header.querySelectorAll('.logo-letter[data-row="p3tro"]');
    const legend = this.header.querySelectorAll('.logo-letter[data-row="legend"]');

    if (Motion.reduced || !Motion.gsap) {
      Motion.forceReveal([this.header]);
      Motion.forceReveal(letters);
      this.bindLogoHover();
      return;
    }

    Motion.gsap.to(this.header, {
      y: 0,
      opacity: 1,
      duration: 0.8,
      ease: 'power3.out',
      force3D: true,
      onComplete: () => Motion.settle([this.header]),
    });
    Motion.gsap.to(p3tro, {
      y: 0,
      opacity: 1,
      stagger: 0.06,
      duration: 0.55,
      delay: 0.18,
      ease: 'power3.out',
    });
    Motion.gsap.to(legend, {
      y: 0,
      opacity: 1,
      stagger: 0.04,
      duration: 0.5,
      delay: 0.48,
      ease: 'power3.out',
      onComplete: () => {
        Motion.settle(letters);
        this.bindLogoHover();
      },
    });

    setTimeout(() => {
      if (!this.header.classList.contains('is-settled')) {
        Motion.forceReveal([this.header]);
        Motion.forceReveal(letters);
        this.bindLogoHover();
      }
    }, 2000);
  }

  // Letters lift 3px on a staggered wave when the logo is hovered.
  bindLogoHover() {
    if (this._logoHoverBound || Motion.reduced || !Motion.gsap) return;
    const logo = this.header.querySelector('.logo');
    const letters = this.header.querySelectorAll('.logo-letter');
    if (!logo || !letters.length) return;
    this._logoHoverBound = true;
    logo.addEventListener('mouseenter', () => {
      Motion.gsap.to(letters, {
        y: -3,
        stagger: 0.03,
        duration: 0.28,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    });
    logo.addEventListener('mouseleave', () => {
      Motion.gsap.to(letters, {
        y: 0,
        stagger: 0.02,
        duration: 0.32,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    });
  }

  updateHeaderOnScroll() {
    const isScrolled = window.scrollY > this.scrollThreshold;

    if (isScrolled) {
      this.header.classList.add(ThemeConfig.classes.isScrolled);
    } else {
      this.header.classList.remove(ThemeConfig.classes.isScrolled);
    }
  }
}

// ============================================================================
// MENU FUNCTIONALITY
// ============================================================================

// Wrap each nav link's text in per-character spans so the stylesheet can
// run the staggered lift + colour-shift on hover (matches the live site:
// no underline bar, letters lift and turn orange in a wave).
class MenuManager {
  constructor() {
    this.menuBtn = document.querySelector(ThemeConfig.selectors.menuBtn);
    this.menuOverlay = document.querySelector(ThemeConfig.selectors.menuOverlay);
    this.body = document.body;

    if (!this.menuBtn || !this.menuOverlay) return;

    this.init();
  }

  init() {
    // When gsap drives the open/hover animation, tag the overlay so the
    // stylesheet's CSS-transition fallback (per-<li> fade, per-link hover)
    // stands down and the two don't stack.
    if (Motion.gsap && !Motion.reduced) this.menuOverlay.classList.add('menu-gsap');

    this.splitLinks();
    this.bindLinkHovers();
    this.menuBtn.addEventListener('click', () => this.toggle());
    this.menuOverlay.addEventListener('click', (e) => {
      if (e.target === this.menuOverlay || e.target.classList.contains('menu-bg')) {
        this.close();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
  }

  // Per-character lift + colour wave on hover, staggered from the outer
  // edge (left column from the start, right column from the end), plus a
  // small horizontal shift of the whole link — matches the live site.
  // Falls back to the CSS transition version if gsap is unavailable.
  bindLinkHovers() {
    if (Motion.reduced || !Motion.gsap) return;
    const links = this.menuOverlay.querySelectorAll('.menu-col a, .menu-col button.linkish');
    links.forEach((link) => {
      const chars = link.querySelectorAll('.menu-link-char');
      if (!chars.length) return;
      const isRight = !!link.closest('.menu-col.right');
      const tl = Motion.gsap.timeline({ paused: true });
      tl.to(
        chars,
        {
          y: -7,
          color: 'var(--orange)',
          stagger: { each: 0.028, from: isRight ? 'end' : 'start' },
          duration: 0.38,
          ease: 'power3.out',
        },
        0
      ).to(link, { x: isRight ? -10 : 10, duration: 0.45, ease: 'power3.out' }, 0);
      link.addEventListener('mouseenter', () => tl.play());
      link.addEventListener('mouseleave', () => tl.reverse());
      // Kill the CSS-transition version so the two don't fight.
      link.dataset.gsapHover = '1';
    });
  }

  splitLinks() {
    const links = this.menuOverlay.querySelectorAll('.menu-col a, .menu-col button.linkish');
    links.forEach((link) => {
      if (link.dataset.menuSplit) return;
      link.dataset.menuSplit = '1';

      const text = (link.textContent || '').trim();
      if (!text) return;

      link.textContent = '';

      const wrap = document.createElement('span');
      wrap.className = 'menu-link-chars';
      const chars = Array.from(text);
      wrap.style.setProperty('--n', chars.length);
      chars.forEach((ch, i) => {
        const span = document.createElement('span');
        span.className = 'menu-link-char';
        span.textContent = ch === ' ' ? ' ' : ch;
        span.style.setProperty('--i', i);
        wrap.appendChild(span);
      });

      link.appendChild(wrap);
    });
  }

  toggle() {
    if (this.isOpen()) this.close();
    else this.open();
  }

  open() {
    // The .is-open class alone opens the overlay (opacity + visibility via
    // the stylesheet), so the menu works even if gsap is throttled. gsap
    // only slides the two columns in.
    this.menuOverlay.classList.add(ThemeConfig.classes.isOpen);
    this.body.classList.add(ThemeConfig.classes.menuOpen);
    this.menuBtn.setAttribute('aria-expanded', 'true');
    this.menuBtn.setAttribute('aria-label', 'Close menu');

    if (Motion.reduced || !Motion.gsap) return;

    if (this._anim) this._anim.kill();
    const left = this.menuOverlay.querySelectorAll(
      '.menu-col:not(.right) h2, .menu-col:not(.right) a'
    );
    const right = this.menuOverlay.querySelectorAll('.menu-col.right h2, .menu-col.right a');

    this._anim = Motion.gsap
      .timeline()
      .fromTo(
        left,
        { x: -40, opacity: 0 },
        { x: 0, opacity: 1, stagger: 0.06, duration: 0.5, ease: 'power3.out' },
        0.05
      )
      .fromTo(
        right,
        { x: 40, opacity: 0 },
        { x: 0, opacity: 1, stagger: 0.06, duration: 0.5, ease: 'power3.out' },
        0.05
      );
  }

  close() {
    this.menuOverlay.classList.remove(ThemeConfig.classes.isOpen);
    this.body.classList.remove(ThemeConfig.classes.menuOpen);
    this.menuBtn.setAttribute('aria-expanded', 'false');
    this.menuBtn.setAttribute('aria-label', 'Menu');

    if (this._anim) this._anim.kill();
    if (Motion.gsap) {
      Motion.gsap.set(this.menuOverlay.querySelectorAll('.menu-col h2, .menu-col a'), {
        clearProps: 'all',
      });
    }
  }

  isOpen() {
    return this.menuOverlay.classList.contains(ThemeConfig.classes.isOpen);
  }
}
// ============================================================================
// SEARCH FUNCTIONALITY
// ============================================================================

class SearchManager {
  constructor() {
    this.searchBtn = document.querySelector(ThemeConfig.selectors.searchBtn);
    this.searchOverlay = document.querySelector(
      ThemeConfig.selectors.searchOverlay
    );
    this.searchInput = this.searchOverlay?.querySelector('[data-search-input]');
    this.searchResults = this.searchOverlay?.querySelector('#search-results');
    this.closeBtn = this.searchOverlay?.querySelector('[data-action="search-close"]');
    this.body = document.body;
    this.currentRequest = null;
    // Server-rendered default product list, shown before typing and
    // restored whenever the input is cleared — captured before any live
    // search result ever overwrites it.
    this.defaultResultsHTML = this.searchResults ? this.searchResults.innerHTML : '';

    if (!this.searchBtn || !this.searchOverlay) return;

    this.init();
  }

  init() {
    this.searchBtn.addEventListener('click', () => this.open());
    this.closeBtn.addEventListener('click', () => this.close());
    this.searchOverlay.addEventListener('click', (e) => {
      if (e.target === this.searchOverlay) this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
    this.searchInput?.addEventListener(
      'input',
      debounce((e) => this.handleSearch(e.target.value), 250)
    );
  }

  open() {
    // .search-overlay.is-open only toggles pointer-events in the stylesheet
    // (visibility:hidden is set separately and never overridden by that
    // class) — the original site's JS set visibility directly, same as the
    // lookbook lightbox.
    this.searchOverlay.style.visibility = 'visible';
    this.searchOverlay.classList.add(ThemeConfig.classes.isOpen);
    if (this.closeBtn) {
      this.closeBtn.style.visibility = 'visible';
      this.closeBtn.style.opacity = '1';
    }

    // Overlay + close button visibility is handled above / by CSS; gsap
    // only slides the input field up.
    if (!Motion.reduced && Motion.gsap) {
      const input = this.searchOverlay.querySelector('.search-panel input');
      if (input) {
        Motion.gsap.fromTo(
          input,
          { y: 24, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.45, ease: 'power3.out', delay: 0.05, clearProps: 'transform' }
        );
      }
    }

    this.searchInput?.focus();
  }

  close() {
    this.searchOverlay.classList.remove(ThemeConfig.classes.isOpen);
    this.searchOverlay.style.visibility = 'hidden';
    if (this.closeBtn) {
      this.closeBtn.style.opacity = '0';
      this.closeBtn.style.visibility = 'hidden';
    }
    if (this.searchInput) this.searchInput.value = '';
    if (this.searchResults) this.searchResults.innerHTML = this.defaultResultsHTML;
  }

  isOpen() {
    return this.searchOverlay.classList.contains(ThemeConfig.classes.isOpen);
  }

  async handleSearch(query) {
    if (!this.searchResults) return;

    if (!query.trim()) {
      this.searchResults.innerHTML = this.defaultResultsHTML;
      return;
    }

    if (this.currentRequest) this.currentRequest.abort();
    const controller = new AbortController();
    this.currentRequest = controller;

    try {
      const url =
        '/search/suggest.json?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product' +
        '&resources[limit]=10' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.title,vendor';

      const response = await fetch(url, { signal: controller.signal });
      const data = await response.json();
      const products = data.resources?.results?.products || [];

      this.render(products);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
      }
    }
  }

  render(products) {
    if (!products.length) {
      this.searchResults.innerHTML =
        '<p style="padding: 16px 0; color: var(--dim); font-size: 12px; letter-spacing: 0.12em;">NO RESULTS FOUND</p>';
      return;
    }

    this.searchResults.innerHTML = products
      .map((product) => {
        const image = product.featured_image?.url || product.image || '';
        const price = product.price_max
          ? this.formatMoney(product.price_max * 100)
          : '';
        return (
          '<a class="search-hit" href="' +
          product.url +
          '">' +
          '<img src="' +
          image +
          '" alt="" />' +
          '<div><strong>' +
          this.escapeHtml(product.title) +
          '</strong>' +
          (product.product_type
            ? '<span>' + this.escapeHtml(product.product_type) + '</span>'
            : '') +
          '</div>' +
          '<span>' +
          price +
          '</span>' +
          '</a>'
        );
      })
      .join('');
  }

  formatMoney(cents) {
    return window.formatMoney(cents);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

// ============================================================================
// CART MANAGEMENT
// ============================================================================

class CartManager {
  constructor() {
    this.cartBtn = document.querySelector(ThemeConfig.selectors.cartBtn);
    this.cartDrawer = document.querySelector(ThemeConfig.selectors.cartDrawer);
    this.cartList = this.cartDrawer?.querySelector('#cart-list');
    this.cartTotal = this.cartDrawer?.querySelector('#cart-total');
    this.checkoutBtn = this.cartDrawer?.querySelector('[data-action="checkout"]');
    this.rowTemplate = document.getElementById('cart-row-template');
    this.bagCount = this.cartBtn?.querySelector('[data-bag-count]');
    // Scoped to .drawer-panel specifically: the backdrop also carries
    // data-action="cart-close" and comes first in the markup, so an
    // unscoped querySelector grabbed the backdrop instead of the actual
    // close button, leaving the visible × with no click handler at all.
    this.closeBtn = this.cartDrawer?.querySelector('.drawer-panel [data-action="cart-close"]');
    this.backdrop = this.cartDrawer?.querySelector('.drawer-backdrop');

    // Discount code field (drawer). The cart page has its own [data-cart-discount].
    this.discountForm = this.cartDrawer?.querySelector('[data-cart-discount]');

    if (!this.cartBtn || !this.cartDrawer) return;

    this.init();
  }

  init() {
    this.cartBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    });
    this.closeBtn?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());
    // Bind every discount form on the page (drawer + cart page).
    document.querySelectorAll('[data-cart-discount]').forEach((f) => this.bindDiscountForm(f));

    if (this.checkoutBtn) {
      this.checkoutBtn.addEventListener('click', () => {
        window.location.href = this.checkoutBtn.dataset.checkoutUrl || '/cart';
      });
    }

    document.addEventListener('cart:change', () => this.refresh());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    this.refresh();
  }

  open() {
    this.cartDrawer.classList.add(ThemeConfig.classes.isOpen);
  }

  close() {
    this.cartDrawer.classList.remove(ThemeConfig.classes.isOpen);
  }

  isOpen() {
    return this.cartDrawer.classList.contains(ThemeConfig.classes.isOpen);
  }

  async refresh() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.render(cart);
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  }

  render(cart) {
    if (this.bagCount) {
      const count = cart.item_count;
      if (count > 0) {
        this.bagCount.textContent = count;
        this.bagCount.classList.add('is-on');
      } else {
        this.bagCount.classList.remove('is-on');
      }
    }

    if (!this.cartList) return;

    this.cartList.querySelectorAll('.cart-row').forEach((row) => row.remove());

    const emptyMsg = this.cartList.querySelector('#cart-empty');

    if (cart.item_count === 0) {
      if (emptyMsg) emptyMsg.style.display = '';
    } else {
      if (emptyMsg) emptyMsg.style.display = 'none';

      cart.items.forEach((item) => {
        const row = this.buildRow(item);
        if (row) this.cartList.appendChild(row);
      });
    }

    if (this.cartTotal) {
      this.cartTotal.textContent = this.formatMoney(cart.total_price);
    }

    // Hide the discount field when the bag is empty; refresh applied codes.
    if (this.discountForm) {
      this.discountForm.hidden = cart.item_count === 0;
      this.renderDiscounts(this.discountForm, cart);
    }
    document.querySelectorAll('[data-cart-discount]').forEach((form) => {
      if (form !== this.discountForm) this.renderDiscounts(form, cart);
    });
  }

  // ---- Discount code -------------------------------------------------------

  bindDiscountForm(form) {
    if (!form || form.dataset.bound) return;
    form.dataset.bound = '1';

    const submit = () => {
      const input = form.querySelector('[data-discount-input]');
      const code = ((input && input.value) || '').trim();
      if (code) this.applyDiscount(form, code);
    };

    // The drawer field is a <div>, the cart-page field is a <div> inside the
    // cart <form> — either way, drive it from the button + Enter key, never
    // a real form submit.
    if (form.tagName === 'FORM') {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        submit();
      });
    }
    form.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-discount-remove]');
      if (remove) {
        e.preventDefault();
        this.applyDiscount(form, '', remove.getAttribute('data-discount-remove') || '');
        return;
      }
      if (e.target.closest('[data-discount-apply]')) {
        e.preventDefault();
        submit();
      }
    });
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-discount-input]')) {
        e.preventDefault();
        submit();
      }
    });
  }

  renderDiscounts(form, cart) {
    const list = form.querySelector('[data-discount-tags]');
    if (!list) return;
    // /cart.js exposes cart.discount_codes: [{ code, amount, type }]
    const codes = (cart.discount_codes || []).filter((d) => d.applicable !== false);
    list.innerHTML = codes
      .map(function (d) {
        return (
          '<li class="cart-discount-tag">' +
          '<span>' + d.code + '</span>' +
          (d.amount
            ? '<span class="cart-discount-amt">−' +
              (window.formatMoney ? window.formatMoney(d.amount) : '') +
              '</span>'
            : '') +
          '<button type="button" data-discount-remove="' + d.code + '" aria-label="Remove ' + d.code + '">×</button>' +
          '</li>'
        );
      })
      .join('');
  }

  async applyDiscount(form, code, removeCode) {
    const input = form.querySelector('[data-discount-input]');
    const btn = form.querySelector('[data-discount-apply]');
    const msg = form.querySelector('[data-discount-msg]');
    const setMsg = (text, isError) => {
      if (!msg) return;
      msg.textContent = text || '';
      msg.hidden = !text;
      msg.classList.toggle('is-error', !!isError);
    };

    if (btn) {
      btn.disabled = true;
      btn.textContent = removeCode ? '…' : 'APPLYING…';
    }
    setMsg('');

    try {
      // Keep any other already-applied codes; Shopify replaces the set.
      let cart = await fetch('/cart.js').then((r) => r.json());
      let codes = (cart.discount_codes || [])
        .filter((d) => d.applicable !== false)
        .map((d) => d.code);
      if (removeCode) {
        codes = codes.filter((c) => c.toLowerCase() !== removeCode.toLowerCase());
      } else if (code && !codes.some((c) => c.toLowerCase() === code.toLowerCase())) {
        codes.push(code);
      }

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      cart = await res.json();

      const nowApplied = (cart.discount_codes || []).filter((d) => d.applicable !== false);
      const wanted = code && code.trim();

      if (removeCode) {
        setMsg('');
      } else if (wanted && !nowApplied.some((d) => d.code.toLowerCase() === wanted.toLowerCase())) {
        setMsg('That code isn’t valid for this order.', true);
      } else if (wanted) {
        setMsg('Code applied.', false);
        if (input) input.value = '';
      }

      document.dispatchEvent(new CustomEvent('cart:change'));
    } catch (err) {
      console.error('[P3TRO LEGEND] Discount code failed:', err);
      setMsg('Something went wrong. Try again.', true);
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'APPLY';
      }
    }
  }

  buildRow(item) {
    if (!this.rowTemplate) return null;

    const fragment = this.rowTemplate.content.cloneNode(true);
    const row = fragment.querySelector('.cart-row');

    const img = row.querySelector('img');
    if (img) {
      img.src = item.image || '';
      img.alt = item.title || '';
    }

    row.querySelectorAll('[data-product-link]').forEach((link) => {
      link.href = item.url || '#';
    });

    const titleEl = row.querySelector('h4 a') || row.querySelector('h4');
    if (titleEl) titleEl.textContent = item.product_title || item.title;

    const colorEl = row.querySelector('[data-color]');
    if (colorEl) {
      colorEl.textContent = item.variant_title || '';
      colorEl.style.display = item.variant_title ? '' : 'none';
    }

    const qtyValueEl = row.querySelector('[data-qty-value]');
    if (qtyValueEl) qtyValueEl.textContent = item.quantity;

    const qtyDecreaseBtn = row.querySelector('[data-qty-decrease]');
    const qtyIncreaseBtn = row.querySelector('[data-qty-increase]');
    if (qtyDecreaseBtn) {
      qtyDecreaseBtn.disabled = item.quantity <= 1;
      qtyDecreaseBtn.addEventListener('click', () => {
        this.changeQuantity(item.key, item.quantity - 1);
      });
    }
    if (qtyIncreaseBtn) {
      qtyIncreaseBtn.addEventListener('click', () => {
        this.changeQuantity(item.key, item.quantity + 1);
      });
    }

    const priceEl = row.querySelector('[data-line-price]');
    if (priceEl) priceEl.textContent = this.formatMoney(item.final_line_price);

    const removeBtn = row.querySelector('[data-remove]');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.changeQuantity(item.key, 0));
    }

    return row;
  }

  async changeQuantity(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
      });
      document.dispatchEvent(new CustomEvent('cart:change'));
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  formatMoney(cents) {
    return window.formatMoney(cents);
  }
}

// ============================================================================
// HERO VIDEO MANAGER
// ============================================================================
// Note: product add-to-cart / variant selection lives inline in
// sections/product-petro.liquid, scoped to that section's markup.

class HeroVideoManager {
  constructor() {
    this.hero = document.querySelector(ThemeConfig.selectors.hero);
    this.muteBtn = document.querySelector(ThemeConfig.selectors.muteBtn);
    this.video = this.hero?.querySelector('video');

    if (!this.hero || !this.video || !this.muteBtn) return;

    this.init();
  }

  init() {
    this.muteBtn.addEventListener('click', () => this.toggleMute());
  }

  toggleMute() {
    const isMuted = this.video.muted;
    this.video.muted = !isMuted;
    this.muteBtn.classList.toggle('is-muted');
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function initManager(ManagerClass) {
  try {
    new ManagerClass();
  } catch (error) {
    // One broken manager must never stop the rest of the theme from
    // initializing.
    console.error('[P3TRO LEGEND] ' + ManagerClass.name + ' failed to initialize:', error);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initManager(HeaderManager);
  initManager(MenuManager);
  initManager(SearchManager);
  initManager(CartManager);
  initManager(HeroVideoManager);
  initManager(RevealManager);
});

// Shopify's Theme Editor swaps section HTML via AJAX when a merchant edits
// settings — DOMContentLoaded does not fire again for that, so newly
// injected sections (e.g. header) need their interactivity rebound here.
document.addEventListener('shopify:section:load', () => {
  initManager(HeaderManager);
  initManager(MenuManager);
  initManager(HeroVideoManager);
  initManager(RevealManager);
});

// In the Theme Editor, a re-rendered section can arrive with the reveal
// CSS still hiding its content and no scroll/rAF to trigger the entrance.
// Show everything immediately whenever the editor swaps or selects a
// section or block, and once shortly after load as a catch-all.
(function () {
  var forceAll = function () {
    try {
      Motion.forceReveal(
        document.querySelectorAll(
          '[data-reveal], .story-line, .hero-title, .mute-btn, .product-card:not(.is-hidden), .lookbook-grid button, .about-copy p, .page-contact-bg img, .pdp-gallery, .pdp-info > *:not(.is-hidden), .story-copy p, .story-quote cite, .story-section-label, [data-quote-line], .story-facts li, .site-header, .site-header .logo-letter'
        )
      );
    } catch (e) {}
  };
  ['shopify:section:load', 'shopify:section:select', 'shopify:section:deselect', 'shopify:block:select', 'shopify:block:deselect'].forEach(
    function (evt) {
      document.addEventListener(evt, function () {
        setTimeout(forceAll, 50);
      });
    }
  );
  if (window.Shopify && window.Shopify.designMode) {
    setTimeout(forceAll, 1200);
  }
})();

// ============================================================================
// SHOPIFY-SPECIFIC UTILITIES
// ============================================================================

/**
 * Add product to cart using Shopify Cart API
 */
window.addToCart = async function (variantId, quantity = 1) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: quantity,
          },
        ],
      }),
    });

    if (response.ok) {
      document.dispatchEvent(new CustomEvent('cart:change'));
      return true;
    }
  } catch (error) {
    console.error('Error adding to cart:', error);
  }
  return false;
};

/**
 * Get current cart data
 */
window.getCart = async function () {
  try {
    const response = await fetch('/cart.js');
    return await response.json();
  } catch (error) {
    console.error('Error fetching cart:', error);
    return null;
  }
};
